import React, { useRef, useState } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Select from '../../../../components/form/AppSelect';
import NumberInput from '../../../../components/form/NumberInput';
import { api } from '../../../../shared/api';

const selectTheme = (theme) => ({
  ...theme,
  colors: { ...theme.colors, primary: '#206bc4', primary25: '#e9f0fb', primary50: '#d3e2f8' },
});

/** Adds N cases of a case type to the project; `onAdd(caseId, count, notes)` resolves to true on success. */
const AddCaseForm = ({ onAdd, onClose }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [count, setCount] = useState(1);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const latestInput = useRef('');

  const search = async (inputValue) => {
    if (!inputValue || inputValue.length < 2) { setOptions([]); return; }
    try {
      const res = await api.get(`warehouses/api/item-cases/?search=${encodeURIComponent(inputValue)}`);
      if (latestInput.current !== inputValue) return;
      setOptions((res.results || res || []).map((c) => ({ value: c.id, label: c.name })));
    } catch (err) {
      console.error('Error searching cases:', err);
    }
  };

  // react-select takes the return value of onInputChange as the new input text, so it must not be a Promise.
  const handleInputChange = (inputValue) => {
    latestInput.current = inputValue;
    search(inputValue);
    return inputValue;
  };

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const added = await onAdd(selected.value, Math.max(1, parseInt(count, 10) || 1), notes);
      if (added) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 rounded-3 border border-primary-subtle p-3">
      <h6 className="fw-semibold mb-3">{t('projects.cases.addCase')}</h6>
      <Row className="g-3 align-items-end">
        <Col md={5}>
          <Form.Label className="fw-medium">{t('projects.cases.caseTemplate')}</Form.Label>
          <Select
            placeholder={t('projects.cases.searchCaseTemplate')}
            options={options}
            value={selected}
            onChange={setSelected}
            onInputChange={handleInputChange}
            isClearable
            isSearchable
            theme={selectTheme}
            noOptionsMessage={({ inputValue }) => (inputValue.length < 2 ? t('projects.cases.typeToSearch') : t('common.noResults'))}
          />
        </Col>
        <Col md={2}>
          <Form.Label className="fw-medium">{t('projects.items.quantity')}</Form.Label>
          <NumberInput min={1} value={count} onChange={setCount} />
        </Col>
        <Col md={3}>
          <Form.Label className="fw-medium">{t('common.notes')}</Form.Label>
          <Form.Control value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={t('projects.cases.notesPlaceholder')} />
        </Col>
        <Col md={2} className="d-flex gap-2">
          <Button variant="primary" onClick={submit} disabled={!selected || saving}>
            {saving ? <Spinner size="sm" /> : t('common.add')}
          </Button>
          <Button variant="outline-secondary" onClick={onClose}>{t('common.cancel')}</Button>
        </Col>
      </Row>
    </div>
  );
};

export default AddCaseForm;
